import { useEffect, useState } from 'react';
import { adminApi } from '../../lib/api';
import { useAdminAuth } from './AdminLayout';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, X } from 'lucide-react';
import { CustomisationEditor } from '../../components/CustomisationEditor';

const empty = {
  name: '', description: '', price: 0, category: '', image_url: '',
  rating: 4.5, is_veg: true, is_available: true, is_featured: false, is_todays_special: false, prep_time: 25,
  customisation_options: { spice_levels: [], portions: [], addons: [] },
};

export default function AdminMenu() {
  const { password } = useAdminAuth();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeTab, setActiveTab] = useState('items'); // 'items' or 'categories'
  const [newCatName, setNewCatName] = useState('');
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const client = adminApi(password);

  const load = () => client.get('/admin/menu').then((r) => setItems(r.data));
  const loadCats = () => client.get('/admin/categories').then((r) => setCategories(r.data));

  useEffect(() => {
    load();
    loadCats();
  }, []);  // eslint-disable-line

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...editing, price: Number(editing.price), rating: Number(editing.rating), prep_time: Number(editing.prep_time) };
      if (editing.id) await client.put(`/admin/menu/${editing.id}`, payload);
      else await client.post('/admin/menu', payload);
      toast.success('Saved');
      setShowForm(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    await client.delete(`/admin/menu/${id}`);
    toast.success('Deleted');
    load();
  };

  const addCategory = async (e) => {
    e.preventDefault();
    const name = newCatName.trim();
    if (!name) return;
    try {
      await client.post('/admin/categories', { name });
      toast.success('Category created');
      setNewCatName('');
      loadCats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create category');
    }
  };

  const toggleCategoryActive = async (cat) => {
    try {
      await client.patch(`/admin/categories/${cat.id}`, { is_active: !cat.is_active });
      toast.success('Status updated');
      loadCats();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const deleteCategory = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      await client.delete(`/admin/categories/${id}`);
      toast.success('Deleted');
      loadCats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const toggleItemAvailability = async (item) => {
    try {
      await client.put(`/admin/menu/${item.id}`, { ...item, is_available: !item.is_available });
      toast.success('Availability updated');
      load();
    } catch {
      toast.error('Failed to update item');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="font-heading font-extrabold text-3xl text-textprimary">Menu Management</h1>
          <p className="text-textsecondary font-body text-sm">
            {activeTab === 'items' ? `${items.length} dishes` : `${categories.length} categories`}
          </p>
        </div>
        {activeTab === 'items' && (
          <button onClick={() => { setEditing({ ...empty, category: categories[0]?.name || '' }); setShowForm(true); }} data-testid="admin-add-item" className="bg-saffron text-white px-4 py-2.5 rounded-2xl font-heading font-semibold shadow-warm flex items-center gap-2">
            <Plus className="w-4 h-4" /> Add Item
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-4 border-b border-borderbeige mb-6">
        <button 
          onClick={() => setActiveTab('items')} 
          className={`pb-2.5 font-heading font-bold text-sm tracking-wide transition-colors border-b-2 ${activeTab === 'items' ? 'border-maroon text-maroon' : 'border-transparent text-textsecondary hover:text-textprimary'}`}
        >
          Dishes ({items.length})
        </button>
        <button 
          onClick={() => setActiveTab('categories')} 
          className={`pb-2.5 font-heading font-bold text-sm tracking-wide transition-colors border-b-2 ${activeTab === 'categories' ? 'border-maroon text-maroon' : 'border-transparent text-textsecondary hover:text-textprimary'}`}
          data-testid="admin-tab-categories"
        >
          Categories ({categories.length})
        </button>
      </div>

      {activeTab === 'items' ? (
        <div className="bg-white rounded-2xl shadow-card border border-borderbeige overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-bgsecondary text-textsecondary font-heading text-xs uppercase">
                <tr>
                  <th className="text-left p-3">Item</th>
                  <th className="text-left p-3">Category</th>
                  <th className="text-right p-3">Price</th>
                  <th className="text-center p-3">Status</th>
                  <th className="text-right p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} className="border-t border-borderbeige">
                    <td className="p-3">
                      <div className="flex items-center gap-3">
                        <img src={it.image_url} alt={it.name} className="w-12 h-12 rounded-lg object-cover" />
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-heading font-semibold text-textprimary">{it.name}</span>
                            {it.is_todays_special && (
                              <span className="text-[10px] font-heading font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300">
                                ⭐ Today's Special
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-textsecondary line-clamp-1 max-w-xs font-body">{it.description}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-3 text-textsecondary font-body">{it.category}</td>
                    <td className="p-3 text-right font-heading font-bold text-maroon">₹{it.price}</td>
                    <td className="p-3 text-center">
                      <button 
                        onClick={() => toggleItemAvailability(it)}
                        data-testid={`admin-toggle-availability-${it.id}`}
                        className={`text-xs font-heading font-semibold px-2 py-1 rounded-full transition-colors border ${it.is_available ? 'bg-fresh/10 text-fresh border-fresh/20 hover:bg-fresh/20' : 'bg-red-50 text-red-600 border-red-200 hover:bg-red-100'}`}
                      >
                        {it.is_available ? 'Available' : 'Unavailable'}
                      </button>
                    </td>
                    <td className="p-3 text-right">
                      <button onClick={() => { setEditing(it); setShowForm(true); }} data-testid={`admin-edit-${it.id}`} className="text-textsecondary hover:text-saffron p-1"><Edit2 className="w-4 h-4" /></button>
                      <button onClick={() => remove(it.id)} data-testid={`admin-delete-${it.id}`} className="text-textsecondary hover:text-maroon p-1 ml-2"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2 bg-white rounded-2xl shadow-card border border-borderbeige overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-bgsecondary text-textsecondary font-heading text-xs uppercase">
                <tr>
                  <th className="text-left p-3">Category Name</th>
                  <th className="text-center p-3">Status</th>
                  <th className="text-right p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {categories.length === 0 && (
                  <tr>
                    <td colSpan={3} className="text-center p-6 text-textsecondary font-body">No categories added</td>
                  </tr>
                )}
                {categories.map((c) => (
                  <tr key={c.id} className="border-t border-borderbeige text-textprimary font-body">
                    <td className="p-3 font-heading font-semibold">{c.name}</td>
                    <td className="p-3 text-center">
                      <button 
                        onClick={() => toggleCategoryActive(c)}
                        data-testid={`admin-toggle-category-${c.id}`}
                        className={`text-xs font-heading font-semibold px-2 py-1 rounded-full transition-colors border ${c.is_active ? 'bg-fresh/10 text-fresh border-fresh/20 hover:bg-fresh/20' : 'bg-red-50 text-red-600 border-red-200 hover:bg-red-100'}`}
                      >
                        {c.is_active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="p-3 text-right">
                      <button 
                        onClick={() => deleteCategory(c.id)} 
                        data-testid={`admin-delete-category-${c.id}`}
                        className="text-textsecondary hover:text-maroon p-1"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="bg-white rounded-2xl p-5 shadow-card border border-borderbeige h-fit">
            <h3 className="font-heading font-bold text-lg text-textprimary mb-4">Add Category</h3>
            <form onSubmit={addCategory} className="space-y-4" data-testid="admin-category-form">
              <div>
                <label className="block text-xs font-heading font-bold text-textsecondary mb-1.5">Category Name</label>
                <input 
                  type="text" 
                  value={newCatName}
                  onChange={(e) => setNewCatName(e.target.value)}
                  placeholder="e.g. Snacks, Drinks"
                  required
                  className="w-full px-3 py-2 border border-borderbeige rounded-xl text-sm font-body focus:outline-none focus:ring-2 focus:ring-saffron/40 bg-white"
                  data-testid="admin-category-input"
                />
              </div>
              <button 
                type="submit" 
                className="w-full bg-saffron text-white py-2.5 rounded-xl font-heading font-bold text-sm shadow-warm transition-transform hover:scale-[1.01]"
                data-testid="admin-category-submit"
              >
                Create Category
              </button>
            </form>
          </div>
        </div>
      )}

      {showForm && editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <form onSubmit={save} className="bg-white rounded-3xl p-4 sm:p-6 max-w-xl w-full shadow-warm my-8" data-testid="admin-item-form">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-heading font-bold text-xl">{editing.id ? 'Edit' : 'New'} Item</h2>
              <button type="button" onClick={() => { setShowForm(false); setEditing(null); }}><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <input required placeholder="Name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="w-full px-3 py-2 border border-borderbeige rounded-xl" data-testid="form-name" />
              <textarea required placeholder="Description" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="w-full px-3 py-2 border border-borderbeige rounded-xl" rows={2} data-testid="form-description" />
              <input required placeholder="Image URL" value={editing.image_url} onChange={(e) => setEditing({ ...editing, image_url: e.target.value })} className="w-full px-3 py-2 border border-borderbeige rounded-xl" data-testid="form-image-url" />
              <div className="grid grid-cols-2 gap-3">
                <input required type="number" step="0.01" placeholder="Price (₹)" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} className="px-3 py-2 border border-borderbeige rounded-xl" data-testid="form-price" />
                <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="px-3 py-2 border border-borderbeige rounded-xl text-sm" data-testid="form-category">
                  {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
                <input type="number" step="0.1" min="0" max="5" placeholder="Rating" value={editing.rating} onChange={(e) => setEditing({ ...editing, rating: e.target.value })} className="px-3 py-2 border border-borderbeige rounded-xl" />
                <input type="number" placeholder="Prep time (min)" value={editing.prep_time} onChange={(e) => setEditing({ ...editing, prep_time: e.target.value })} className="px-3 py-2 border border-borderbeige rounded-xl" />
              </div>
              <div className="flex flex-wrap gap-4 text-sm font-body">
                <label className="flex items-center gap-2"><input type="checkbox" checked={editing.is_veg} onChange={(e) => setEditing({ ...editing, is_veg: e.target.checked })} /> Veg</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={editing.is_available} onChange={(e) => setEditing({ ...editing, is_available: e.target.checked })} /> Available</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={editing.is_featured} onChange={(e) => setEditing({ ...editing, is_featured: e.target.checked })} /> Featured</label>
                <label className="flex items-center gap-2 font-semibold text-amber-700 bg-amber-50 px-2 py-1 rounded-lg border border-amber-200">
                  <input type="checkbox" checked={!!editing.is_todays_special} onChange={(e) => setEditing({ ...editing, is_todays_special: e.target.checked })} /> ⭐ Today's Special
                </label>
              </div>
              <CustomisationEditor
                value={editing.customisation_options || { spice_levels: [], portions: [], addons: [] }}
                onChange={(v) => setEditing({ ...editing, customisation_options: v })}
              />
            </div>
            <button type="submit" className="mt-5 w-full bg-saffron text-white py-3 rounded-2xl font-heading font-bold shadow-warm" data-testid="form-submit">Save</button>
          </form>
        </div>
      )}
    </div>
  );
}
